import { useEffect, useState } from "react"
import { Navigate, useNavigate, useParams } from "react-router-dom"
import AsciiArt from "@/components/AsciiArt/AsciiArt"
import Button from "@/components/Button/Button"
import GridBackdrop from "@/components/GridBackdrop/GridBackdrop"
import LokaMark from "@/components/LokaMark/LokaMark"
import PaperNote from "@/components/PaperNote/PaperNote"
import Spinner from "@/components/Spinner/Spinner"
import StatusScreen from "@/components/StatusScreen/StatusScreen"
import { SCATTER_CYCLE } from "@/content/ascii"
import { SESSION_GONE_FOR_GUESTS } from "@/content/archive"
import { UNTITLED_SESSION } from "@/content/customers"
import { useFirestoreSubmit } from "@/hooks/useFirestoreWrite"
import { useExpiryTick } from "@/hooks/useExpiryTick"
import { usePreloadedImages } from "@/hooks/usePreloadedImages"
import { getSession } from "@/services/sessions"
import { joinSessionAsMe } from "@/store/sessionSync"
import { colors } from "@/theme/colors"
import { describeWriteError } from "@/utils/firestoreWrite"
import { sessionPath } from "@/utils/routePaths"
import { isJoinable } from "@/utils/sessionLifecycle"
import { isRemoved } from "@/utils/removal"
import { getStoredParticipantId } from "@/utils/sessionIdentity"
import { useAuthStore } from "@/store/authStore"
import { selectAuthChecking, selectAuthUid } from "@/store/authStore"
import type { FamilyNumber } from "@/theme/colors"
import type { Session } from "@/types"
import styles from "@/styles/pages/Join.module.css"

const EYEBROW = "Digital AI Cards workshop session"
const INTRO_LABEL = "Your seat"
const NAME_REQUIRED = "Add your name so the room knows who you are."
const DEALING = "Dealing the deck."

/**
 * The six cards scattered behind the note.
 *
 * These are the deck's family COVERS, and they are real artwork — a glass render
 * per family with the family's name set on it. Unlike a card front, there is
 * nothing to reproduce in CSS here, so they ship as images. The family tint stays
 * behind each one as the colour that shows while the webp loads and if it never
 * does.
 */
const FAN = [
  // Three down each side of the centre note. Monitors (family 5) was retired, so
  // Acts (family 4) moves up to the top-right slot it left, keeping the two
  // clusters even at 3 and 3.
  { family: 2, left: "20%", top: "22%", rotate: "-8deg", width: 173 },
  { family: 1, left: "8%", top: "48%", rotate: "-12deg", width: 179 },
  { family: 3, left: "22%", top: "74%", rotate: "6deg", width: 174 },
  { family: 4, left: "80%", top: "22%", rotate: "9deg", width: 171 },
  { family: 6, left: "93%", top: "48%", rotate: "5deg", width: 173 },
  { family: 7, left: "82%", top: "76%", rotate: "-8deg", width: 176 },
] as const

/**
 * The small ASCII glyphs between the cards. Text, not artwork, so they cost
 * nothing to load and are the only decoration on the screen that scales with the
 * type rather than with the viewport.
 *
 * Positions are hand-placed to stay clear of the centre column, which is the one
 * thing on this screen anybody has to read.
 */
const SCATTER = [
  { left: "4%", top: "58%", family: 4 },
  { left: "14%", top: "56%", family: 6 },
  { left: "3%", top: "88%", family: 3 },
  { left: "23%", top: "88%", family: 1 },
  { left: "33%", top: "76%", family: 2 },
  { left: "44%", top: "74%", family: 7 },
  { left: "54%", top: "80%", family: 3 },
  { left: "66%", top: "72%", family: 7 },
  { left: "70%", top: "56%", family: 2 },
  { left: "77%", top: "48%", family: 3 },
  { left: "89%", top: "72%", family: 6 },
  { left: "96%", top: "84%", family: 4 },
] as const

/**
 * The same six files, preloaded before the screen mounts so the deal-in
 * animation flies real artwork rather than empty tinted rectangles that fill a
 * beat later. Module-level because `usePreloadedImages` takes it as an effect
 * dependency.
 */
const FAN_SOURCES = FAN.map((card) => `/cards-web/c${card.family}-0.webp`)

/**
 * Below this width the fan is `display: none` (Join.module.css) — there is no
 * animation to protect and no reason to pull half a megabyte of artwork onto a
 * phone that will never draw it, so the wait is skipped entirely. Keep in step
 * with the stylesheet's media query.
 *
 * Read once, at mount: rotating a phone mid-join does not need to restart a
 * preload, and a fan that appears without one still loads normally.
 */
const FAN_VISIBLE_QUERY = "(width > 720px)"

type Resolved =
  | { state: "loading" }
  | { state: "missing" }
  | { state: "failed"; message: string }
  | { state: "found"; session: Session }

export default function Join() {
  const { sessionId } = useParams<{ sessionId: string }>()
  const navigate = useNavigate()
  const uid = useAuthStore(selectAuthUid)
  const authChecking = useAuthStore(selectAuthChecking)

  // A route match guarantees the param, but the type allows undefined — derive the
  // starting state from it rather than correcting it from inside an effect.
  const [resolved, setResolved] = useState<Resolved>(
    sessionId ? { state: "loading" } : { state: "missing" }
  )
  const [name, setName] = useState("")
  const [jobRole, setJobRole] = useState("")
  const [nameError, setNameError] = useState<string | null>(null)
  const { submit, pending, error } = useFirestoreSubmit<string>()

  /*
   * Starts on the first render, alongside the session read rather than after it,
   * so the two waits overlap and the artwork is usually there by the time the
   * link has resolved.
   */
  const [fanVisible] = useState(() => window.matchMedia(FAN_VISIBLE_QUERY).matches)
  const fanReady = usePreloadedImages(FAN_SOURCES, fanVisible)

  useEffect(() => {
    if (!sessionId) return
    let live = true
    // A one-shot read: the link resolves once, and nothing about the session
    // changes what this screen asks for while someone types their name.
    getSession(sessionId)
      .then((session) => {
        if (!live) return
        setResolved(session ? { state: "found", session } : { state: "missing" })
      })
      .catch((caught: unknown) => {
        if (live) setResolved({ state: "failed", message: describeWriteError(caught) })
      })
    return () => {
      live = false
    }
  }, [sessionId])

  // Already joined from this browser: a reload walks straight back in rather
  // than asking again and adding a second row to the roster.
  useEffect(() => {
    if (resolved.state !== "found") return
    if (getStoredParticipantId(resolved.session.id)) {
      navigate(sessionPath(resolved.session.id), { replace: true })
    }
  }, [resolved, navigate])

  /*
   * The session is read once, so nothing here re-renders when its deadline
   * passes — and someone typing their name into an expired room would have the
   * join write refused with no explanation. The tick is what turns the deadline
   * into a render.
   */
  const now = useExpiryTick([resolved.state === "found" ? resolved.session.expiresAt : null])

  /*
   * The link and the auth answer are waited on together. Both are needed before
   * this screen can say anything: `uid` is what tells the facilitator of this
   * session apart from a guest, and answering early would put the name form in
   * front of whoever is running the room for the beat before Firebase restores
   * their session — which is now a screen they can be sent to, not only one they
   * can type the address of.
   */
  if (resolved.state === "loading" || authChecking) {
    return <StatusScreen eyebrow="Join" title="Finding the session." waiting />
  }

  if (resolved.state === "failed") {
    return (
      <StatusScreen
        eyebrow="Join"
        title="Could not reach the session."
        detail="Check the connection and try the link again."
        raw={resolved.message}
      >
        <Button onClick={() => window.location.reload()}>Try again</Button>
      </StatusScreen>
    )
  }

  if (resolved.state === "missing") {
    return (
      <StatusScreen
        eyebrow="Join"
        title="There is no session at that link."
        detail="The link may be mistyped or the session has been deleted. Ask the facilitator to send it again."
      />
    )
  }

  const session = resolved.session

  /*
   * The facilitator of this session has no business on the join screen. The two
   * experiences are separate, and joining from here would put whoever is running
   * the room on the roster as a guest.
   */
  if (uid !== null && session.facilitatorIds.includes(uid)) {
    return <Navigate to={sessionPath(session.id)} replace />
  }

  /*
   * The three ways a session stops admitting people, told apart because they are
   * different news: one is over, one simply timed out and the facilitator can
   * reopen it, and one was taken out of the lists on purpose.
   *
   * A guest is told the session ENDED, not that it was archived or deleted.
   * Which shelf a facilitator filed it on is bookkeeping the room has no stake
   * in, and "deleted" would read to a participant as though their work went with
   * it — it did not.
   */
  if (!isJoinable(session, now)) {
    const title = isRemoved(session)
      ? "This session has ended."
      : session.status === "completed"
        ? "This session has finished."
        : "This link has expired."
    const detail = isRemoved(session)
      ? SESSION_GONE_FOR_GUESTS
      : session.status === "completed"
        ? "The room has wrapped up. Ask the facilitator for the write-up."
        : "Sessions stay open for a day. Ask the facilitator to reopen it or start a new one."

    return <StatusScreen eyebrow="Join" title={title} detail={detail} />
  }

  /*
   * Last, and only on the path that actually draws the fan: every other outcome
   * of the link — finished, expired, facilitator — is news, and holding news
   * behind decoration would be absurd. Here the artwork IS the screen, so the
   * form waits for it and then mounts once, with the deal-in animation starting
   * on the same frame as the images it flies.
   */
  if (!fanReady) {
    return <StatusScreen eyebrow="Join" title={DEALING} waiting />
  }

  const ready = name.trim().length > 0

  const join = async () => {
    if (!ready) {
      setNameError(NAME_REQUIRED)
      return
    }
    setNameError(null)
    // Awaited deliberately: we cannot route into a session without the id this
    // returns, and the roster must contain us before the session screen loads.
    const participantId = await submit(() =>
      joinSessionAsMe(session.id, { name: name.trim(), jobRole: jobRole.trim() })
    )
    if (participantId) navigate(sessionPath(session.id), { replace: true })
  }

  return (
    <div className={styles.page}>
      <GridBackdrop />

      <LokaMark className={styles.mark} />

      <div className={styles.fan} aria-hidden="true">
        {FAN.map((card) => (
          <div
            className={styles.fanCard}
            key={card.family}
            style={{ left: card.left, top: card.top, width: card.width }}
          >
            <div
              className={styles.fanFace}
              style={{
                transform: `rotate(${card.rotate})`,
                backgroundColor: colors.family[card.family].cover,
                backgroundImage: `url(/cards-web/c${card.family}-0.webp)`,
              }}
            />
          </div>
        ))}

        {SCATTER.map((glyph, index) => (
          <div
            className={styles.glyph}
            key={`${glyph.left}-${glyph.top}`}
            style={{ left: glyph.left, top: glyph.top }}
          >
            <AsciiArt
              motif={SCATTER_CYCLE[index % SCATTER_CYCLE.length]}
              family={glyph.family as FamilyNumber}
              size="sm"
            />
          </div>
        ))}
      </div>

      <div className={styles.card}>
        <p className={styles.eyebrow}>{EYEBROW}</p>
        <h1 className={styles.title}>{session.name || UNTITLED_SESSION}</h1>

        <PaperNote size="card" className={styles.note}>
          <p className={styles.noteLabel}>{INTRO_LABEL}</p>
          <input
            className={styles.noteName}
            placeholder="Your name"
            aria-label="Your name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") void join()
            }}
            autoFocus
          />
          <input
            className={styles.noteRole}
            placeholder="Product, Ops, CTO"
            aria-label="Your title, optional"
            value={jobRole}
            onChange={(event) => setJobRole(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") void join()
            }}
          />
        </PaperNote>

        {nameError !== null ? <p className={styles.error}>{nameError}</p> : null}
        {error !== null ? <p className={styles.error}>{error}</p> : null}

        <button
          type="button"
          className={ready ? styles.join : `${styles.join} ${styles.joinIdle}`}
          disabled={pending}
          aria-busy={pending || undefined}
          onClick={join}
        >
          {pending ? <Spinner size="lg" /> : null}
          {pending ? "Joining…" : "Join room"}
          <span aria-hidden="true">→</span>
        </button>
      </div>
    </div>
  )
}
